import { SearchableSelect } from "@/components/ui/searchable-select";
import { RecordProblem } from "@/components/patterns";
import { useCallRecord } from "@/components/calling/CallLink";
import React, { useCallback, useEffect, useState } from "react";
import { useRecordCrumb } from "@/components/shell/ShellContext";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { api, unwrapArray, unwrapObject } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { useAuth } from "@/context/AuthContext";
import { FormSkeleton } from "@/components/ui/page-skeletons";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { WhatsAppChatDialog } from "@/components/whatsapp/WhatsAppChatDialog";
import { TaskDialog } from "@/components/tasks/TaskDialog";
import type { LeadAssignmentHistoryItem, Lead360, MasterLookupRecord } from "@/types";
import type { Task } from "@/types/task";
import { LeadRecordHeader } from "./record/LeadRecordHeader";
import { FollowUpBar } from "./record/FollowUpBar";
import { LeadProperties } from "./record/LeadProperties";
import { CustomerLocation } from "@/components/visits/CustomerLocation";
import { LeadEnquiryCard } from "@/components/ai/LeadEnquiryCard";
import { LeadActivity } from "./record/LeadActivity";
import { LeadRelated } from "./record/LeadRelated";

type Match = { id: string; name: string; owner: string | null };

export function LeadDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { success, error: toastError } = useToast();
  const { hasPermission } = useAuth();
  const callRecord = useCallRecord();

  const [data, setData] = useState<Lead360 | null>(null);
  useRecordCrumb(data ? `${data.lead.first_name || ""} ${data.lead.last_name || ""}`.trim() || data.lead.company_name : undefined);
  const [matches, setMatches] = useState<Match[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [version, setVersion] = useState(0);

  const [waOpen, setWaOpen] = useState(false);
  const [pickNumber, setPickNumber] = useState(false);
  const [taskOpen, setTaskOpen] = useState(false);

  // Convert
  const [convertOpen, setConvertOpen] = useState(false);
  const [convertForm, setConvertForm] = useState({ deal_name: "", deal_amount: 0, expected_close_date: "", link: "NEW" });

  // Disqualify
  const [dqOpen, setDqOpen] = useState(false);
  const [dqReasons, setDqReasons] = useState<MasterLookupRecord[]>([]);
  const [dq, setDq] = useState({ reason: "", notes: "" });

  // Reassign
  const [reassignOpen, setReassignOpen] = useState(false);
  const [reps, setReps] = useState<{ id: string; first_name: string; last_name?: string }[]>([]);
  const [newOwner, setNewOwner] = useState("");

  const [problem, setProblem] = useState<unknown>(null);
  const fetch360 = useCallback(async () => {
    if (!id) return;
    try {
      setProblem(null);
      const res = await api.get(`/api/Lead/360/${id}`);
      const payload = unwrapObject<Lead360>(res);
      setData(payload);
      if (payload?.lead) {
        setConvertForm((f) => ({
          ...f,
          deal_name: `${payload.lead.company_name} - Deal`,
          deal_amount: Number(payload.lead.expected_budget || payload.lead.estimated_value || 0),
          expected_close_date: new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10),
        }));
      }
      setVersion((v) => v + 1);
    } catch (err: any) {
      setProblem(err);
    } finally {
      setLoading(false);
    }
  }, [id, navigate, toastError]);

  useEffect(() => {
    fetch360();
  }, [fetch360]);

  useEffect(() => {
    if (!id) return;
    api
      .get(`/api/Lead/AccountMatches/${id}`)
      .then((r) => {
        const list = unwrapArray<Match>(r);
        setMatches(list);
        // Linking to the account that already exists is the safer default
        setConvertForm((f) => ({ ...f, link: list[0] ? list[0].id : "NEW" }));
      })
      .catch(() => setMatches([]));
  }, [id]);

  if (problem) return <RecordProblem error={problem} what="lead" record={{ type: "LEAD", id }} backTo="/leads" backLabel="Back to leads" onRetry={fetch360} />;

  if (loading || !data) {
    return (
      <div className="space-y-6 p-6">
        <FormSkeleton />
      </div>
    );
  }

  const { lead, tasks, whatsapp, inquiries = [] } = data;
  const name = `${lead.first_name || ""} ${lead.last_name || ""}`.trim() || lead.company_name;
  const phone = lead.mobile || lead.phone;
  // A lead with two different numbers asks which one to call; with one number it calls that
  const sameNumber = !!lead.mobile && !!lead.phone && (lead.mobile.replace(/\D/g, "").slice(-10) === lead.phone.replace(/\D/g, "").slice(-10));
  const startCall = () => {
    if (lead.mobile && lead.phone && !sameNumber) setPickNumber(true);
    else void callRecord("LEAD", lead.id, phone, lead.mobile ? "mobile" : lead.phone ? "phone" : undefined);
  };
  const callField = (field: "mobile" | "phone") => {
    setPickNumber(false);
    void callRecord("LEAD", lead.id, lead[field], field);
  };
  const history: LeadAssignmentHistoryItem[] = (lead as any).assignment_history || [];
  const received = inquiries.map((q) => q.received_at).sort();
  const canAssign = hasPermission("lead.assign");

  const changeStatus = async (status: string, extra: Record<string, any> = {}) => {
    setBusy(true);
    try {
      await api.patch(`/api/Lead/ChangeStatus/${lead.id}`, { status, ...extra });
      await fetch360();
    } catch (err: any) {
      toastError("Could not change the status", err.message || "Try again");
      throw err;
    } finally {
      setBusy(false);
    }
  };

  const submitConvert = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/api/Convert/Lead/${lead.id}`, {
        create_deal: true,
        deal_name: convertForm.deal_name.trim() || undefined,
        deal_amount: Number(convertForm.deal_amount) || undefined,
        expected_close_date: convertForm.expected_close_date ? new Date(convertForm.expected_close_date).toISOString() : undefined,
        existing_account_id: convertForm.link !== "NEW" ? convertForm.link : undefined,
      });
      success("Lead converted", "An account, contact and deal are ready.");
      setConvertOpen(false);
      await fetch360();
    } catch (err: any) {
      toastError("Could not convert", err.message || "Try again");
    } finally {
      setBusy(false);
    }
  };

  const openDisqualify = () => {
    setDq({ reason: "", notes: "" });
    setDqOpen(true);
    if (!dqReasons.length) api.get("/api/MasterLookup/ByCategory/LEAD_DISQUALIFY_REASON").then((r) => setDqReasons(unwrapArray<MasterLookupRecord>(r))).catch(() => undefined);
  };

  const openReassign = () => {
    setNewOwner("");
    setReassignOpen(true);
    if (!reps.length) api.get("/api/User/Assignable?for=lead").then((r) => setReps(unwrapArray(r))).catch(() => undefined);
  };

  const submitReassign = async () => {
    setBusy(true);
    try {
      await api.patch(`/api/Lead/Reassign/${lead.id}`, { owner_id: newOwner, reason: "REASSIGNMENT" });
      success("Lead reassigned", "The new owner has it now.");
      setReassignOpen(false);
      await fetch360();
    } catch (err: any) {
      toastError("Could not reassign", err.message || "Try again");
    } finally {
      setBusy(false);
    }
  };

  const doneTask = async (t: Task) => {
    try {
      await api.patch(`/api/Task/Complete/${t.id}`, { outcome: "Completed from the lead" });
      await fetch360();
    } catch (err: any) {
      toastError("Could not complete the task", err.message || "Try again");
    }
  };

  return (
    <div className="flex flex-col gap-3">
      <LeadRecordHeader
        lead={lead}
        name={name}
        busy={busy}
        can={{ update: hasPermission("lead.update"), convert: hasPermission("lead.convert"), whatsapp: hasPermission("whatsapp.send") }}
        onCall={startCall}
        onWhatsApp={() => setWaOpen(true)}
        onEmail={() => window.open(`mailto:${lead.email}`)}
        onEdit={() => navigate(`/leads/${lead.id}/edit`)}
        onDisqualify={openDisqualify}
        onConvert={() => setConvertOpen(true)}
        onStep={(s) => changeStatus(s).catch(() => undefined)}
        untouchedDays={data.untouched_days}
      />

      {(params.get("followup") === "1" || lead.lead_status === "FOLLOW_UP") && <FollowUpBar leadId={lead.id} status={lead.lead_status} canUpdate={hasPermission("lead.update")} onChanged={() => void fetch360()} />}

      <div className="grid grid-cols-1 items-start gap-3 @4xl/main:grid-cols-12">
        <div className="order-2 min-w-0 space-y-3 @4xl/main:order-1 @4xl/main:col-span-4 @6xl/main:col-span-3">
          <LeadProperties lead={lead} firstInquiry={received[0]} lastInquiry={received[received.length - 1]} reasons={data.score_reasons} />
          <CustomerLocation key={lead.id} type="LEAD" id={lead.id} lat={(lead as any).geo_lat} lng={(lead as any).geo_lng} setAt={(lead as any).geo_set_at} canEdit={hasPermission("lead.update") && hasPermission("visit.create")} />
        </div>
        <div className="order-1 min-w-0 space-y-3 @4xl/main:order-2 @4xl/main:col-span-8 @6xl/main:col-span-6">
          <LeadEnquiryCard key={lead.id} leadId={lead.id} canUpdate={hasPermission("lead.update")} hasEnquiry={!!(lead.requirement_notes || lead.product_interest || inquiries.length)} onApplied={() => void fetch360()} />
          <LeadActivity
            lead={lead}
            tasks={tasks}
            whatsapp={whatsapp}
            inquiries={inquiries}
            reloadKey={version}
            onCall={() => undefined}
            onEmail={() => window.open(`mailto:${lead.email}`)}
            onWhatsApp={() => setWaOpen(true)}
            onTask={() => setTaskOpen(true)}
          />
        </div>
        <div className="order-3 min-w-0 @4xl/main:col-span-12 @6xl/main:col-span-3">
          <LeadRelated
            lead={lead}
            tasks={tasks}
            inquiries={inquiries}
            history={history}
            matches={matches}
            canAssign={canAssign}
            canTask={hasPermission("activity.create")}
            canMerge={hasPermission("lead.update")}
            onMerged={(keptId) => (keptId === lead.id ? void fetch360() : navigate(`/leads/${keptId}`, { replace: true }))}
            onReassign={openReassign}
            onTask={() => setTaskOpen(true)}
            onDoneTask={doneTask}
          />
        </div>
      </div>

      {/* Convert */}
      <Dialog open={convertOpen} onOpenChange={setConvertOpen}>
        <DialogContent className="sm:max-w-md">
          <form onSubmit={submitConvert}>
            <DialogHeader>
              <DialogTitle>Convert to a deal</DialogTitle>
              <DialogDescription>Creates the contact and the deal, and the account unless you link to one that exists.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3.5 py-4 text-sm">
              <div className="space-y-1.5 rounded-lg bg-muted/40 p-3 text-xs">
                {matches.map((m) => (
                  <label key={m.id} className="flex cursor-pointer items-start gap-2">
                    <input type="radio" name="link" className="mt-0.5 accent-primary" checked={convertForm.link === m.id} onChange={() => setConvertForm((f) => ({ ...f, link: m.id }))} />
                    <span>
                      Link to the existing account <strong>{m.name}</strong>
                      {m.owner ? ` (owned by ${m.owner})` : ""}
                    </span>
                  </label>
                ))}
                <label className="flex cursor-pointer items-start gap-2">
                  <input type="radio" name="link" className="mt-0.5 accent-primary" checked={convertForm.link === "NEW"} onChange={() => setConvertForm((f) => ({ ...f, link: "NEW" }))} />
                  <span>
                    Create a new account <strong>{lead.company_name}</strong>
                  </span>
                </label>
                <div>
                  Contact created: <strong>{name}</strong>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="conv_deal_name">Deal name</Label>
                <Input id="conv_deal_name" value={convertForm.deal_name} onChange={(e) => setConvertForm((p) => ({ ...p, deal_name: e.target.value }))} required />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="conv_amount">Deal value (₹)</Label>
                  <Input id="conv_amount" type="number" value={convertForm.deal_amount} onChange={(e) => setConvertForm((p) => ({ ...p, deal_amount: Number(e.target.value) }))} />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="conv_close">Expected close</Label>
                  <Input id="conv_close" type="date" value={convertForm.expected_close_date} onChange={(e) => setConvertForm((p) => ({ ...p, expected_close_date: e.target.value }))} />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setConvertOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={busy}>
                {busy ? "Converting…" : "Convert"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Disqualify */}
      <Dialog open={dqOpen} onOpenChange={setDqOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Disqualify this lead</DialogTitle>
            <DialogDescription>It leaves your lists but stays on record with the reason.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <SearchableSelect value={dq.reason} onChange={(e) => setDq((d) => ({ ...d, reason: e.target.value }))} className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm">
              <option value="">Choose a reason</option>
              {dqReasons.map((r) => (
                <option key={r.code} value={r.name}>
                  {r.name}
                </option>
              ))}
            </SearchableSelect>
            <textarea rows={3} value={dq.notes} onChange={(e) => setDq((d) => ({ ...d, notes: e.target.value }))} placeholder="Notes (optional)" className="w-full resize-none rounded-md border border-input bg-background px-3 py-2 text-sm outline-none" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDqOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" disabled={busy || !dq.reason} onClick={() => changeStatus("DISQUALIFIED", { disqualify_reason: dq.reason, disqualify_notes: dq.notes || undefined }).then(() => setDqOpen(false)).catch(() => undefined)}>
              Disqualify
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reassign */}
      <Dialog open={reassignOpen} onOpenChange={setReassignOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Give this lead to someone</DialogTitle>
            <DialogDescription>The new owner is notified and the change is kept in the assignment history.</DialogDescription>
          </DialogHeader>
          <SearchableSelect value={newOwner} onChange={(e) => setNewOwner(e.target.value)} className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm">
            <option value="">Choose a person</option>
            {reps.map((u) => (
              <option key={u.id} value={u.id}>{`${u.first_name} ${u.last_name || ""}`.trim()}</option>
            ))}
          </SearchableSelect>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReassignOpen(false)}>
              Cancel
            </Button>
            <Button disabled={busy || !newOwner} onClick={submitReassign}>
              Reassign
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

        <Dialog open={pickNumber} onOpenChange={setPickNumber}>
          <DialogContent className="sm:max-w-sm">
            <DialogHeader>
              <DialogTitle>Which number?</DialogTitle>
              <DialogDescription>{name} has two numbers. Choose the one to call.</DialogDescription>
            </DialogHeader>
            <div className="grid gap-2">
              <Button type="button" variant="outline" onClick={() => callField("mobile")}>Mobile · {lead.mobile}</Button>
              <Button type="button" variant="outline" onClick={() => callField("phone")}>Landline · {lead.phone}</Button>
            </div>
          </DialogContent>
        </Dialog>
      {phone && (
        <WhatsAppChatDialog
          open={waOpen}
          onOpenChange={(v) => {
            setWaOpen(v);
            if (!v) fetch360();
          }}
          phone={phone}
          name={name}
          leadId={lead.id}
        />
      )}

      <TaskDialog
        open={taskOpen}
        onOpenChange={setTaskOpen}
        defaultRelated={{ type: "LEAD", id: lead.id, name }}
        onSaved={() => {
          setTaskOpen(false);
          fetch360();
        }}
      />
    </div>
  );
}
